export interface Config {
  name: string;
  value: string;
  disabled: boolean;
}

export interface PayerOption {
  payerId: string;
  careType: string;
  payerType: string;
  name: string;
  value: string;
  checked: false;
}

export interface PayerConfig {
  payerId: string;
  payerName: string;
  payerType: string;
  payerSubtype: string;
  benefitType: string;
  authorizationRequired: boolean;
  plans: Plan[];
  isActive: boolean;
  // CO-3392/CO-3398: present on new-model (classified) payer records.
  state?: string;
}

export interface Plan {
  planName: string;
  newPlanName?: string;
  address1: string;
  city: string;
  state: string;
  zipCode: string;
  zipCodeExtension: string;
  value: string;
  // zip?: string,
  // address2: string,
}

export interface PatientBenefits {
  activePrimaryBenefitsId?: string;
  benefitsId: string;
  benefitsVersion: number;
  billingDetails: BillingDetails[];
  tenantId: number;
  patientId: number;
  patientChartId: number;
  payerId: string;
  payerType: string;
  payerName: string;
  payerPrimacyLevel: string;
  payerPrimacyLevelTitle: string;
  subscriberId: string;
  effectiveDate: string;
  expiredDate: string;
  addNewPayerPlan: boolean;
  eligibilityVerified: boolean;
  isVBID: boolean;
  medicaidNumber: string;
  medicareNumber: string;
  medicaidPending: boolean;
  plan: Plan;
  readyToBill: string;
  benefitType: string;
  status: string;
  reasonForHold: string;
  createdDatetime: string;
  updatedDatetime: string;
  subscriber: Subscriber;
  financial: Financial;
  hospiceEligibility: HospiceEligibility;
  roomAndBoardEligibility: RoomAndBoardEligibility;
  showDetails: boolean;
  authorizations: any[];
  // planAddress: string,
  // planAddressOther: string,
  // planDescription: string,
}

export interface Subscriber {
  relationship: string;
  subscriberId: string;
  groupNumber: string;
  firstName: string;
  lastName: string;
  middleInitial: string;
  dateOfBirth: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  zipCodeExtension: string;
  phoneNumber: string;
  emailAddress: string;
  additionalInfo: string;
  isVBID: boolean;
  subscriberHistory: SubscriberHistory[];
}

export interface SubscriberHistory {
  subscriberId: string;
  effectiveDate: string;
  expiredDate: string;
}

export interface Financial {
  coPay: number;
  limitAmount: number;
  deductible: number;
  maxVisit: number;
}

export interface HospiceEligibility {
  routineHomeCareHighDaysUsed: number;
  hospiceBenefitElectionDate: string;
  admitBenefitPeriod: number;
  admitBenefitPeriodStartDate: string;
  // admitHospiceEpisode: number,
  previousHospiceStartDate: string;
  previousHospiceDischargeDate: string;
  dateOfFinalBill: string;
}

export interface RoomAndBoardEligibility {
  admitBenefitPeriod: number;
  admitBenefitPeriodStartDate: string;
  patientLiability: Liabilities[];
}

export interface Liabilities {
  patientLiability: boolean;
  liabilityAmount: number;
  from: string;
  to: string;
}

export interface PatientBenefitActionEvent {
  patientBenefit: PatientBenefits;
  action: string;
}

export interface BillingDetails {
  start: string;
  end: string;
  billRate: string;
  careLevel: number;
}

export const payerTypeList: Config[] = [
  { name: "Commercial", value: "COMMERCIAL", disabled: false },
  { name: "Medicaid", value: "MEDICAID", disabled: false },
  { name: "Medicare", value: "MEDICARE", disabled: false },
];

// export const payerSubtypeList: Config[] = [
//     { name: '(09) Self-Pay', value: 'SELF', disabled: false },
//     { name: '(11) Other Non-Federal-Programs', value: 'OTHER_NON_FEDERAL', disabled: false },
//     { name: '(12) Preferred Provider Organization (PPO)', value: 'PPO', disabled: false },
//     { name: '(14) Exclusive Provider Organization (EPO)', value: 'EPO', disabled: false },
//     { name: '(15) Indemnity Insurance', value: 'INDEMNITY_INSURANCE', disabled: false },
//     { name: '(16) Health Maintenance Organization (HMO) Medicare Risk', value: 'HMO', disabled: false },
//     { name: '(BL) Blue Cross/Blue Shield', value: 'BL', disabled: false },
//     { name: '(CH) CHAMPUS', value: 'CHAMPUS', disabled: false },
//     { name: '(CI) Commercial Insurance Co.', value: 'CI', disabled: false },
//     { name: '(HM) Health Maintenance Organization', value: 'HM', disabled: false },
//     { name: '(MA) Medicare Part A', value: 'MA', disabled: false },
//     { name: '(MB) Medicare Part B', value: 'MB', disabled: false },
//     { name: '(MC) Medicaid', value: 'MC', disabled: false },
//     { name: '(OF) Other Federal Program', value: 'OTHER_FEDERAL', disabled: false },
//     { name: '(VA) Veterans Affairs Plan', value: 'VA', disabled: false },
//     { name: '(WC) Workers Compensation Health Claim', value: 'WC', disabled: false },
// ];

export const relationshipList: Config[] = [
  { name: "Self", value: "SELF", disabled: false },
  { name: "Spouse", value: "SPOUSE", disabled: false },
  { name: "Parent", value: "PARENT", disabled: false },
  { name: "Other", value: "OTHER", disabled: false },
];

export const stateList: string[] = [
  "AL",
  "AK",
  "AS",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FM",
  "FL",
  "GA",
  "GU",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MH",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "MP",
  "OH",
  "OK",
  "OR",
  "PW",
  "PA",
  "PR",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VI",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

export const authorizationTypeList: Config[] = [
  { name: "Effective Dates", value: "EFFECTIVE_DATES", disabled: false },
  { name: "Visit Count", value: "VISIT_COUNT", disabled: false },
  //{ name: 'Care Location', value: 'CARE_LOCATION', disabled: false },
  //{ name: 'DME', value: 'DME', disabled: false },
  //{ name: 'Exceeds Days on Service', value: 'EXCEEDS_DAY_ON_SERVICE', disabled: false },
  //{ name: 'Level of Care', value: 'LEVEL_OF_CARE', disabled: false },
  //{ name: 'Other', value: 'OTHER', disabled: false },
];

export interface PatientEligibilityData {
  payerName: string;
  payerPrimacyLevelTitle: string;
  runDate: string;
  runBy: string;
  status: string;
  response: null;
  patientChartId: number;
  dataType: string;
  payerId: number;
  benefitType: string;
  rawOutput: string;
  dateRun: string;
}



// WEBPACK FOOTER //
// ./src/pages/patient-chart/patient-benefits/models/index.ts